import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';

import { FacetPill } from 'src/components/atoms/FacetPill/FacetPill';
import { HorizontalScroller } from 'src/components/molecules/HorizontalScroller/HorizontalScroller';
import type { UIFacet } from 'src/components/types/UIFacet';
import * as styles from './DynamicFacetPills.styles';

export interface DynamicFacetPillsProps {
  facets: UIFacet[];
  /** Called the first time the row is moved, by swipe or by arrow, and not again. */
  onFirstScroll?: () => void;
  /** Called once, when the row first reaches the viewport. */
  onImpression?: () => void;
  onPillClick: (facetKey: string) => void;
  openFacetKey?: string | null;
}

/** No options, or every count explicitly zero. A `null` count is unknown, not zero. */
const hasNoResults = (facet: UIFacet): boolean =>
  facet.options.every((option) => option.count === 0);

export const DynamicFacetPills = ({
  facets,
  onFirstScroll,
  onImpression,
  onPillClick,
  openFacetKey = null,
}: DynamicFacetPillsProps) => {
  const impressionRef = useImpressionRef<HTMLDivElement>(() => onImpression?.());

  if (facets.length === 0) {
    return null;
  }

  return (
    <HorizontalScroller
      ariaLabel="Filters"
      onFirstScroll={onFirstScroll}
      testId="dynamic-facet-pills"
    >
      <div css={styles.pillList} data-testid="dynamic-facet-pills-row" ref={impressionRef}>
        {facets.map((facet) => (
          <FacetPill
            activeCount={facet.options.filter((option) => option.active).length}
            disabled={hasNoResults(facet)}
            icon={facet.key === openFacetKey ? 'expanded' : 'collapsed'}
            key={facet.key}
            onClick={() => onPillClick(facet.key)}
            title={facet.title}
          />
        ))}
      </div>
    </HorizontalScroller>
  );
};
